// src/pages/HomePage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Lightbulb, Star, Heart, ArrowRight, Mail, Truck, ShieldCheck } from 'lucide-react';
import HeroSection from '@/components/HeroSection.jsx';
import StoryCard from '@/components/StoryCard.jsx';
import TestimonialCard from '@/components/TestimonialCard.jsx';
import SEOHead from '@/components/SEOHead.jsx';
import { organizationSchema, localBusinessSchema, websiteSchema, breadcrumbSchema } from '@/lib/schemaMarkup.js';
import { useProducts, useCategories } from '@/hooks/useProducts.js';

const fmt = (n) => `R${Number(n).toLocaleString('en-ZA', { minimumFractionDigits: 0 })}`;

// ---------- Category Card with Crossfade & Text Below Image ----------
function CategoryCarouselCard({ category, delay }) {
  const images = category.sample_images || [];
  
  // Slideshow hook (same as ShopPage)
  function useSlideshow(images, intervalMs = 6000) {
    const [index, setIndex] = useState(() => Math.floor(Math.random() * images.length));
    const timerRef = useRef(null);
    useEffect(() => {
      if (images.length < 2) return;
      timerRef.current = setInterval(() => {
        setIndex(prev => (prev + 1) % images.length);
      }, intervalMs);
      return () => clearInterval(timerRef.current);
    }, [images.length, intervalMs]);
    return index;
  }
  
  const imgIndex = useSlideshow(images, 6000);
  const imageUrl = images.length > 0 ? images[imgIndex] : null;

  // Fallback when no images
  if (!imageUrl) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay }}
        className="group relative rounded-[16px] overflow-hidden bg-white premium-shadow hover:-translate-y-1 transition-all duration-300"
      >
        <Link to={`/shop?category=${category.slug}`} className="block">
          <div className="relative h-56 overflow-hidden bg-[hsl(var(--warm-sand))]/20 flex items-center justify-center text-4xl text-[hsl(var(--charcoal))]/20">
            🧸
          </div>
          <div className="p-4 text-center">
            <h3 className="text-xl font-bold text-[hsl(var(--charcoal))]">{category.name}</h3>
            {category.product_count !== undefined && (
              <p className="text-[hsl(var(--charcoal))]/70 text-sm mt-1">{category.product_count} products</p>
            )}
            <p className="text-[hsl(var(--charcoal))]/70 text-sm mt-2 line-clamp-2">
              {category.description || 'Explore our collection'}
            </p>
            <span className="inline-block mt-3 text-[hsl(var(--primary))] font-medium text-sm group-hover:underline">
              Shop Now →
            </span>
          </div>
        </Link>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
      className="group relative rounded-[16px] overflow-hidden bg-white premium-shadow hover:-translate-y-1 transition-all duration-300"
    >
      <Link to={`/shop?category=${category.slug}`} className="block">
        {/* Image area with crossfade slideshow */}
        <div className="relative h-56 overflow-hidden bg-[hsl(var(--warm-sand))]/20">
          <div className="relative w-full h-full">
            <AnimatePresence mode="crossfade">
              <motion.img
                key={imgIndex}
                src={imageUrl}
                alt={category.name}
                width="600"
                height="400"
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover"
                initial={{ opacity: 0, scale: 1.05 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.8 }}
              />
            </AnimatePresence>
          </div>

          {/* Optional: image counter dots (like ShopPage) */}
          {images.length > 1 && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
              {images.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-500 ${
                    i === imgIndex ? 'w-4 bg-white' : 'w-1.5 bg-white/40'
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Content area – title and description below image */}
        <div className="p-4 text-left">
          <h3 className="text-xl font-bold text-[hsl(var(--charcoal))] mb-0.5 group-hover:text-[hsl(var(--primary))] transition-colors">
            {category.name}
          </h3>
          {category.product_count !== undefined && (
            <p className="text-[hsl(var(--charcoal))]/70 text-xs mb-2">{category.product_count} products</p>
          )}
          <p className="text-[hsl(var(--charcoal))]/70 text-sm leading-relaxed line-clamp-2">
            {category.description || 'Explore our collection'}
          </p>
          <span className="inline-block mt-3 text-[hsl(var(--primary))] font-medium text-sm group-hover:underline">
            Shop Now →
          </span>
        </div>
      </Link>
    </motion.div>
  );
}

// ---------- Skeleton for Featured Grid ----------
function FeaturedSkeleton({ className }) {
  return (
    <div className={`${className} rounded-[16px] overflow-hidden bg-[hsl(var(--soft-grey))] animate-pulse premium-shadow`} />
  );
}

// ---------- Main HomePage Component ----------
export default function HomePage() {

  // Fetch categories with product counts AND sample images for the carousel
  const {
    categories,
    loading: categoriesLoading,
    error: categoriesError
  } = useCategories({ withCounts: true, withImages: true });

  // Fetch featured products for the "Trending Essentials" grid
  const { products: featured, loading: featuredLoading } = useProducts({ featured: true, limit: 6 });

  // Helper for featured products
  const hero    = featured[0] ?? null;
  const wide    = featured[1] ?? null;
  const small1  = featured[2] ?? null;
  const small2  = featured[3] ?? null;

  const imgSrc = (p) => p?.images?.[0]?.src || p?.featured_image || null;
  const hasDiscount = (p) => p?.compare_price && p.compare_price > p.price;

  // Animation variants for the value banner
  const featureBannerVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.15, duration: 0.6, ease: "easeOut" }
    })
  };

  return (
    <>
      <SEOHead 
        title="Premium Wooden Toys & Pet Products South Africa | Hublu"
        description="Award-winning premium wooden toys, Montessori education products, and pet accessories for modern South African families. Free shipping nationwide for orders above R1,000."
        canonical="https://hubluonline.co.za/"
      />

      <HeroSection />

      {/* VALUE PROPOSITION BANNER */}
      <div className="bg-[hsl(var(--charcoal))] text-white py-8 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row flex-wrap justify-start items-start md:items-center gap-6 md:gap-12 text-sm md:text-base font-medium">
          
          <motion.div 
            custom={1}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            whileHover={{ scale: 1.05, x: 4 }}
            variants={featureBannerVariants}
            className="flex items-center gap-4 cursor-default"
          >
            <div className="w-12 h-12 rounded-full bg-[hsl(var(--primary))]/20 flex items-center justify-center shrink-0 animate-float-gentle">
              <Truck className="w-5 h-5 text-[hsl(var(--primary))]" />
            </div>
            <span className="leading-snug">Free Shipping Nationwide South Africa<br className="hidden md:block"/> for orders above R1,000</span>
          </motion.div>
          
          <motion.div 
            custom={2}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            whileHover={{ scale: 1.05, x: 4 }}
            variants={featureBannerVariants}
            className="flex items-center gap-4 cursor-default"
          >
            <div className="w-12 h-12 rounded-full bg-[hsl(var(--secondary))]/20 flex items-center justify-center shrink-0 animate-float-gentle" style={{ animationDelay: '0.5s' }}>
              <Star className="w-5 h-5 text-[hsl(var(--secondary))]" />
            </div>
            <span className="leading-snug">Premium Quality<br className="hidden md:block"/> Products</span>
          </motion.div>

          <motion.div 
            custom={3}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            whileHover={{ scale: 1.05, x: 4 }}
            variants={featureBannerVariants}
            className="flex items-center gap-4 cursor-default"
          >
            <div className="w-12 h-12 rounded-full bg-[hsl(var(--accent))]/20 flex items-center justify-center shrink-0 animate-float-gentle" style={{ animationDelay: '1s' }}>
              <ShieldCheck className="w-5 h-5 text-[hsl(var(--accent))]" />
            </div>
            <span className="leading-snug">Honest Materials &<br className="hidden md:block"/> Intentional Design</span>
          </motion.div>

        </div>
      </div>

      {/* FEATURED CATEGORIES SECTION — DYNAMIC FROM DB */}
      <section className="py-24 bg-[hsl(var(--cream-white))] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center md:text-left">
            <motion.h2 
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold text-[hsl(var(--charcoal))] inline-block relative mb-4"
            >
              Toys & Pet Products for South African Families
              <motion.div 
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="absolute -bottom-2 left-0 right-0 h-1.5 bg-[hsl(var(--secondary))] origin-left"
              />
            </motion.h2>
            <p className="text-[hsl(var(--charcoal))]/70 max-w-2xl mt-4">
              Discover our curated collections featuring intentional design and honest materials, built to last and inspire.
            </p>
          </div>

          {categoriesLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="rounded-[16px] overflow-hidden bg-[hsl(var(--soft-grey))] animate-pulse h-72" />
              ))}
            </div>
          ) : categoriesError ? (
            <div className="text-center py-12 text-red-500">Failed to load categories</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {categories.slice(0, 4).map((category, idx) => (
                <CategoryCarouselCard key={category.category_id} category={category} delay={idx * 0.1} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FEATURED PRODUCTS GRID — live from DB */}
      <section className="bg-[hsl(var(--soft-grey))] pt-16 pb-24 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[hsl(var(--charcoal))] mb-4">Trending Essentials</h2>
            <p className="text-[hsl(var(--charcoal))]/70 max-w-2xl mx-auto">Handpicked favorites loved by kids, pets, and parents alike.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 auto-rows-[250px]">

            {/* Large hero tile */}
            {featuredLoading || !hero ? (
              <FeaturedSkeleton className="md:col-span-2 md:row-span-2" />
            ) : (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="md:col-span-2 md:row-span-2 rounded-[16px] overflow-hidden relative group cursor-pointer premium-shadow"
              >
                {imgSrc(hero) && (
                  <img
                    src={imgSrc(hero)}
                    alt={hero.images?.[0]?.alt || hero.title}
                    width="800"
                    height="800"
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                )}
                {hero.is_new && (
                  <div className="absolute top-4 right-4 bg-[hsl(var(--accent))] text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-sm z-10">
                    New Arrival
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent p-8 flex flex-col justify-end">
                  {hasDiscount(hero) && (
                    <span className="inline-block bg-[hsl(var(--primary))] text-white text-xs font-bold px-2 py-1 rounded-full w-fit mb-2">
                      Sale — {Math.round((1 - hero.price / hero.compare_price) * 100)}% off
                    </span>
                  )}
                  <h3 className="text-2xl font-bold text-white mb-1">{hero.title}</h3>
                  <p className="text-white/80 mb-1 text-sm">{hero.category_name}</p>
                  <p className="text-[hsl(var(--primary))] font-bold text-lg mb-4">
                    {fmt(hero.price)}
                    {hasDiscount(hero) && (
                      <span className="ml-2 text-white/50 line-through text-sm font-normal">{fmt(hero.compare_price)}</span>
                    )}
                  </p>
                  <Link to={`/shop/${hero.id}`} className="inline-flex items-center text-white font-medium hover:text-[hsl(var(--primary))] transition-colors w-max">
                    View Details <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            )}

            {/* Wide tile */}
            {featuredLoading || !wide ? (
              <FeaturedSkeleton className="md:col-span-2 md:row-span-1" />
            ) : (
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="md:col-span-2 md:row-span-1 rounded-[16px] overflow-hidden relative group cursor-pointer premium-shadow"
              >
                {imgSrc(wide) && (
                  <img
                    src={imgSrc(wide)}
                    alt={wide.images?.[0]?.alt || wide.title}
                    width="800"
                    height="400"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                )}
                {wide.is_new && (
                  <div className="absolute top-3 right-3 bg-[hsl(var(--accent))] text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-sm z-10">
                    New
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent p-6 flex flex-col justify-end">
                  <h3 className="text-xl font-bold text-white mb-1">{wide.title}</h3>
                  <div className="flex items-center justify-between">
                    <span className="text-[hsl(var(--primary))] font-semibold">{fmt(wide.price)}</span>
                    <Link to={`/shop/${wide.id}`} className="text-white/80 font-medium text-sm hover:text-[hsl(var(--primary))] transition-colors">Shop Now</Link>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Small tile 1 */}
            {featuredLoading || !small1 ? (
              <FeaturedSkeleton className="md:col-span-1 md:row-span-1" />
            ) : (
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="md:col-span-1 md:row-span-1 rounded-[16px] overflow-hidden relative group cursor-pointer premium-shadow"
              >
                {imgSrc(small1) && (
                  <img
                    src={imgSrc(small1)}
                    alt={small1.images?.[0]?.alt || small1.title}
                    width="400"
                    height="400"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                )}
                {small1.is_new && (
                  <div className="absolute top-3 right-3 bg-[hsl(var(--accent))] text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-sm z-10">
                    New
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent p-6 flex flex-col justify-end">
                  <h3 className="text-lg font-bold text-white mb-1 line-clamp-2">{small1.title}</h3>
                  <Link to={`/shop/${small1.id}`} className="text-[hsl(var(--primary))] text-sm font-medium hover:underline">View</Link>
                </div>
              </motion.div>
            )}

            {/* Small tile 2 */}
            {featuredLoading || !small2 ? (
              <FeaturedSkeleton className="md:col-span-1 md:row-span-1" />
            ) : (
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.6 }}
                className="md:col-span-1 md:row-span-1 rounded-[16px] overflow-hidden relative group cursor-pointer premium-shadow"
              >
                {imgSrc(small2) && (
                  <img
                    src={imgSrc(small2)}
                    alt={small2.images?.[0]?.alt || small2.title}
                    width="400"
                    height="400"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                )}
                {small2.is_new && (
                  <div className="absolute top-3 right-3 bg-[hsl(var(--accent))] text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-sm z-10">
                    New
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent p-6 flex flex-col justify-end">
                  <h3 className="text-lg font-bold text-white mb-1 line-clamp-2">{small2.title}</h3>
                  <Link to={`/shop/${small2.id}`} className="text-[hsl(var(--primary))] text-sm font-medium hover:underline">View</Link>
                </div>
              </motion.div>
            )}

          </div>

          <div className="text-center mt-12">
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-full border-2 border-[hsl(var(--charcoal))]/20 text-[hsl(var(--charcoal))] font-semibold hover:border-[hsl(var(--accent))] hover:text-[hsl(var(--accent))] transition-all duration-300"
            >
              View All Products <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS SECTION */}
      <section className="py-24 bg-[hsl(var(--cream-white))] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold text-[hsl(var(--charcoal))] mb-4"
            >
              Loved by South Africans
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-[hsl(var(--charcoal))]/70 max-w-2xl mx-auto"
            >
              Hear from parents, educators, and pet lovers who have brought Hublu into their homes.
            </motion.p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <TestimonialCard 
              quote="The quality is unmatched. These toys genuinely support fine motor skills and independent learning in my classroom."
              name="Sarah van der Merwe"
              role="Montessori Educator"
              delay={0.2}
            />
            <TestimonialCard 
              quote="Finally, toys that don't break after a week! Beautiful, aesthetic, and my kids play with them daily."
              name="Thandi Nkosi"
              role="Mother of two"
              delay={0.4}
            />
            <TestimonialCard 
              quote="Finding a bed that offers actual support for large breeds without flattening out is rare. This bed hits the perfect balance of premium comfort and durability, and it looks great in the living room."
              name="David Botha"
              role="Dog Trainer"
              delay={0.6}
            />
          </div>
        </div>
      </section>

      {/* STORYTELLING SECTION */}
      <section className="py-24 bg-[hsl(var(--warm-sand))] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[hsl(var(--charcoal))] mb-4">The Hublu Promise</h2>
            <p className="text-[hsl(var(--charcoal))]/80 max-w-2xl mx-auto">We believe in honest materials and intentional design that works better and lasts longer.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <StoryCard 
              icon={Lightbulb} 
              title="Intentional Design" 
              description="Open-ended designs that inspire cognitive growth and imaginative play without batteries." 
              colorClass="text-[hsl(var(--accent))]"
              delay={0.2}
            />
            <StoryCard 
              icon={Star} 
              title="Premium Quality" 
              description="Sustainable hardwoods, non-toxic finishes, and durable materials meant to be passed down." 
              colorClass="text-[hsl(var(--secondary))]"
              delay={0.4}
            />
            <StoryCard 
              icon={Heart} 
              title="Trusted by Families" 
              description="Rigorous safety testing ensures complete peace of mind for you, your kids, and your pets." 
              colorClass="text-[hsl(var(--primary))]"
              delay={0.6}
            />
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-32 bg-gradient-to-br from-[hsl(var(--natural-oak))] to-[hsl(var(--warm-sand))] relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold text-white mb-6"
          >
            Ready to bring joy home?
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-white/90 text-lg md:text-xl mb-10"
          >
            Join thousands of happy families enjoying premium quality and 100% free shipping nationwide across South Africa for orders above R1,000.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link to="/shop" className="inline-block px-10 py-5 text-xl font-bold rounded-full bg-[hsl(var(--primary))] text-white shadow-[0_0_25px_rgba(255,87,34,0.5)] hover:shadow-[0_0_40px_rgba(255,87,34,0.8)] hover:bg-[hsl(16,100%,45%)] hover:-translate-y-1 transition-all duration-300">
              Start Shopping Now
            </Link>
            <Link to="/contact" className="inline-flex items-center px-10 py-5 text-xl font-bold rounded-full bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm transition-all duration-300">
              <Mail className="w-5 h-5 mr-2" /> Contact Us
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}